
import { defineComponent, onMounted, computed, ref } from 'vue';
import { isLoaded, state } from '@/interfaces/RemoteData';
import { compactOverview,
  fetchOverview,
  getProcessedIndices,
  getTotalFilteredCount } from '@/api/explorer';
import SectionHeader from '@/components/global/SectionHeader.vue';
import ExplorerNavigation from '@/components/explorer/ExplorerNavigation.vue';
import RemoteContent from '@/components/global/RemoteContent.vue';
import useExplorerFilters from '@/components/explorer/explorerFilters';
import { useExplorerSorting } from '@/components/explorer/explorerSorting';
import HowToQuote from '@/components/global/HowToQuote.vue';
import ExplorerTable from '@/components/explorer/ExplorerTable.vue';

function debounce<T extends(...args: any[]) => void>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    setTimeout(() => func(...args), wait);
  }) as T;
}

export default defineComponent({
  components: {
    HowToQuote,
    SectionHeader,
    ExplorerNavigation,
    RemoteContent,
    ExplorerTable,
  },
  setup() {
    const showFilters = ref(false);

    const { filterValues, updateFilter, resetFilters } = useExplorerFilters();
    const { sortKey, sortAsc, sortBy } = useExplorerSorting();

    const debouncedUpdateFilter = debounce((key: string, value: [number, number]) => {
      updateFilter(key, value);
    }, 150);

    const optimizedRows = computed(() => {
      if (compactOverview.value.state !== state.Success || !compactOverview.value.payload) {
        return [];
      }

      const compactData = compactOverview.value.payload;
      const processedIndices = getProcessedIndices(
        filterValues.value,
        sortKey.value,
        sortAsc.value,
      );

      // Map nur die verarbeiteten Indizes zu lesbaren Zeilen
      return processedIndices.map((index) => {
        const compactRow = compactData[index];
        if (!compactRow) {
          return null;
        }
        return {
          sura: compactRow.s,
          verse: compactRow.v,
          readings_count: compactRow.r,
          references_count: compactRow.rf,
          sources_count: compactRow.sr,
          variants_count: compactRow.vr,
          manuscripts_count: compactRow.m,
          intertexts_count: compactRow.i,
        };
      }).filter((row) => row !== null);
    });

    const totalFilteredRows = computed(() => {
      if (!isLoaded(compactOverview.value)) {
        return 0;
      }
      return getTotalFilteredCount(filterValues.value);
    });

    const toggleFilters = () => {
      showFilters.value = !showFilters.value;
    };

    onMounted(() => {
      fetchOverview();
    });

    return {
      compactOverview,
      isLoaded,
      sortKey,
      sortAsc,
      sortBy,
      optimizedRows,
      totalFilteredRows,
      filterValues,
      debouncedUpdateFilter,
      resetFilters,
      showFilters,
      toggleFilters,
    };
  },
});
